import type { TreeNodeBranch } from '../types';
import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';
import type { ComponentProps } from 'react';

import React, { useCallback } from 'react';

import useClassy from '@core/hooks/useClassy';
import { useSuperHubStore } from '@core/store';

import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Truncate from '@ui/Truncate';

import useBuildBranchPath from '../../hooks/useBuildBranchPath';
import { useRedirectPendingModalContext } from '../../Modals';
import Tooltip from '../Tooltip';
import TreeConnector from '../TreeConnector';

import styles from './Branch.module.scss';

/** Default height of a branch item in the virtual list. */
export const BRANCH_ITEM_HEIGHT = 28;

/**
 * Generates the unique DOM ID string based for a given branch.
 * @example
 * ```ts
 * 'BranchItem-/branches/1.0_some-branch-name'
 * ```
 */
export function getBranchItemId(branch: BranchRepresentationType) {
  return `BranchItem-${branch.uri}`;
}

interface BranchItemProps extends ComponentProps<'div'> {
  node: TreeNodeBranch;
}

export function BranchItem({ node, ...props }: BranchItemProps) {
  const bem = useClassy(styles, 'BranchItem');
  const buildVersionPath = useBuildBranchPath();
  const currentBranch = useSuperHubStore(s => s.currentBranch);
  const { setRedirectPendingModalValue } = useRedirectPendingModalContext();

  const { data: branch } = node;
  const formattedBranchName = `v${branch.name}`;
  const isCurrent = branch.name === currentBranch.name;
  const isLastItem = node.position === node.positionLength - 1;
  const path = buildVersionPath(branch.name);

  const handleLinkClick = useCallback(
    event => {
      if (isCurrent) {
        event.preventDefault();
        return;
      }

      // Show redirect modal while browser slowly navigates to this version.
      setRedirectPendingModalValue(`v${branch.name}`);
    },
    [isCurrent, setRedirectPendingModalValue, branch.name],
  );

  return (
    <div
      {...props}
      aria-label={formattedBranchName}
      aria-level={node.nestingLevel + 1}
      aria-posinset={node.position + 1}
      aria-selected={isCurrent}
      aria-setsize={node.positionLength}
      className={bem('&', isCurrent && '_current')}
      id={getBranchItemId(node.data)}
      role="treeitem"
    >
      <TreeConnector kind={isLastItem ? 'end' : 'middle'} />
      <a className={bem('-link')} href={path} onClick={handleLinkClick}>
        <Truncate>{formattedBranchName}</Truncate>
      </a>
      <Tooltip content="Coming soon!">
        <Button aria-label="Edit Branch" className={bem('-edit-button')} kind="secondary" size="xs" text>
          <Icon aria-hidden="true" name="more-vertical" />
        </Button>
      </Tooltip>
    </div>
  );
}
